import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import * as api from '../../api';

const money = (kobo) => `₦${Math.round(Number(kobo || 0) / 100).toLocaleString('en-NG')}`;
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];

function RenewalBadge({ row }) {
  const cfg = row.renewalStatus === 'expired'
    ? ['#ef4444', 'rgba(239,68,68,.12)']
    : row.renewalStatus === 'danger'
      ? ['#f59e0b', 'rgba(245,158,11,.12)']
      : row.renewalStatus === 'trial'
        ? ['var(--ap-text-dim)', 'var(--ap-surface-2)']
        : ['#10b981', 'rgba(16,185,129,.12)'];
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, color: cfg[0], background: cfg[1] }}>{row.renewalLabel}</span>;
}

function RequestQueue({ statusFilter, setStatusFilter, requests, busyId, onApprove, onReject }) {
  return (
    <div className="ap-panel">
      <div className="ap-panel-head">
        <h3>Business plan payments</h3>
        <select className="ap-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="pending">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>
      {requests.length === 0 ? <div className="ap-empty">No {statusFilter === 'all' ? '' : statusFilter} payment requests.</div> : requests.map(r => (
        <div key={r.requestId} className="ap-request-card" style={{ marginBottom: 10 }}>
          <div className="ap-request-row">
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="ap-request-name">{r.businessName} → {r.requestedTier}{r.billingCycle ? ` (${r.billingCycle})` : ''}</div>
              <div className="ap-request-meta">{money(r.amountKobo)} · submitted by {r.submittedBy}</div>
              <div className="ap-request-date">{new Date(r.submittedAt).toLocaleString()}</div>
              {r.paymentNote && <div className="ap-request-desc">Payment note: {r.paymentNote}</div>}
              {r.status === 'rejected' && r.rejectionReason && <div className="ap-request-desc" style={{ color: 'var(--ap-danger)' }}>Rejected: {r.rejectionReason}</div>}
              {r.status !== 'pending' && r.reviewedAt && <div className="ap-request-date">{r.status === 'approved' ? '✓ Approved' : '✕ Rejected'} by {r.reviewedBy || 'admin'} · {new Date(r.reviewedAt).toLocaleString()}</div>}
            </div>
            {r.status === 'pending' && <div className="ap-btn-row">
              <button className="ap-btn ap-btn-approve" disabled={busyId === r.requestId} onClick={() => onApprove(r.requestId)}>Approve payment</button>
              <button className="ap-btn ap-btn-reject" disabled={busyId === r.requestId} onClick={() => onReject(r)}>Reject</button>
            </div>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SubscriptionBillingTab({ showAlert, onPendingCountChange }) {
  const [overview, setOverview] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [search, setSearch] = useState('');

  const loadPendingBadge = useCallback(async () => {
    const biz = await api.adminGetUpgradeRequests('pending').catch(() => ({ pendingCount: 0 }));
    onPendingCountChange?.(biz?.pendingCount || 0);
  }, [onPendingCountChange]);

  const loadOverview = useCallback(async () => {
    try { setOverview(await api.adminGetSubscriptionOverview()); }
    catch (e) { showAlert(e.message, 'error'); }
  }, [showAlert]);

  const loadRequests = useCallback(async () => {
    try {
      const r = await api.adminGetUpgradeRequests(statusFilter);
      setRequests(r.requests || []);
    } catch (e) { showAlert(e.message, 'error'); }
  }, [statusFilter, showAlert]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadOverview(), loadRequests(), loadPendingBadge()]);
    setLoading(false);
  }, [loadOverview, loadRequests, loadPendingBadge]);

  useEffect(() => { refresh(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.adminApproveUpgradeRequest(id);
      showAlert('Payment approved and the subscription has been updated.', 'success');
      await refresh();
    } catch (e) { showAlert(e.message, 'error'); }
    setBusyId(null);
  };

  const reject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.requestId);
    try {
      await api.adminRejectUpgradeRequest(rejectTarget.requestId, rejectReason);
      showAlert('Payment request rejected.', 'success');
      setRejectTarget(null); setRejectReason('');
      await refresh();
    } catch (e) { showAlert(e.message, 'error'); }
    setBusyId(null);
  };

  const subscriptions = useMemo(() => (overview?.subscriptions || []).filter(r => {
    const q = search.trim().toLowerCase();
    return !q || r.businessName.toLowerCase().includes(q) || r.ownerEmail.toLowerCase().includes(q) || r.subscriptionTier.toLowerCase().includes(q);
  }), [overview, search]);
  const s = overview?.summary || {};

  return <>
    <div className="ap-page-head">
      <div>
        <h2>Subscriptions &amp; Billing</h2>
        <div className="ap-sub">Plan payments, renewal countdowns, subscription health and recurring revenue in one place.</div>
      </div>
      <button className="ap-refresh-btn" onClick={refresh}>↻ Refresh</button>
    </div>

    <div className="ap-stat-grid">
      <div className="ap-stat ap-stat-hero" style={{ '--stat-color': '#10B981' }}><div className="ap-stat-label">Lifetime plan revenue</div><div className="ap-stat-value">{money(s.totalRevenueKobo)}</div></div>
      <div className="ap-stat" style={{ '--stat-color': '#3B82F6' }}><div className="ap-stat-label">This month</div><div className="ap-stat-value">{money(s.monthRevenueKobo)}</div></div>
      <div className="ap-stat" style={{ '--stat-color': '#8B5CF6' }}><div className="ap-stat-label">This year</div><div className="ap-stat-value">{money(s.yearRevenueKobo)}</div></div>
      <div className="ap-stat" style={{ '--stat-color': '#10B981' }}><div className="ap-stat-label">Active paid businesses</div><div className="ap-stat-value">{s.activePaidBusinesses || 0}</div></div>
      <div className="ap-stat" style={{ '--stat-color': '#F59E0B' }}><div className="ap-stat-label">Expiring ≤ 5 days</div><div className="ap-stat-value">{s.expiringWithinFiveDays || 0}</div></div>
      <div className="ap-stat" style={{ '--stat-color': '#EF4444' }}><div className="ap-stat-label">Expired / downgrade due</div><div className="ap-stat-value">{s.expiredAwaitingDowngrade || 0}</div></div>
      <div className="ap-stat" style={{ '--stat-color': '#F59E0B' }}><div className="ap-stat-label">Payments awaiting review</div><div className="ap-stat-value">{s.pendingPayments || 0}</div></div>
    </div>

    <div className="ap-panel-grid-2">
      <div className="ap-panel">
        <div className="ap-panel-head"><h3>Plan revenue by month</h3></div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={overview?.monthlyRevenue || []}><CartesianGrid strokeDasharray="3 3" stroke="var(--ap-border)" /><XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} tickFormatter={v => `₦${Math.round(v / 100000) / 10}k`} /><Tooltip formatter={v => money(v)} /><Bar dataKey="amountKobo" fill="#10B981" name="Revenue" radius={[5,5,0,0]} /></BarChart>
        </ResponsiveContainer>
      </div>
      <div className="ap-panel">
        <div className="ap-panel-head"><h3>Revenue by plan</h3></div>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart><Pie data={overview?.revenueByTier || []} dataKey="amountKobo" nameKey="tier" outerRadius={85} label={({ tier }) => tier}>{(overview?.revenueByTier || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => money(v)} /></PieChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div className="ap-panel" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="ap-panel-head" style={{ padding: '18px 20px 0' }}>
        <h3>Business renewal monitor</h3>
        <input className="ap-search" placeholder="Search business, owner or plan…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="ap-table-wrap">
        <table className="ap-table"><thead><tr><th>Business</th><th>Plan</th><th>Billing</th><th>Renewal date</th><th>Countdown</th><th>Payment review</th></tr></thead>
          <tbody>{subscriptions.map(row => <tr key={row.businessId}>
            <td><div className="ap-biz-name">{row.businessName}</div><div className="ap-rank-meta">{row.ownerEmail}</div></td>
            <td style={{ textTransform: 'capitalize' }}>{row.subscriptionTier}</td><td style={{ textTransform: 'capitalize' }}>{row.billingCycle}</td>
            <td>{row.subscriptionRenewsAt ? new Date(row.subscriptionRenewsAt).toLocaleDateString() : '—'}</td><td><RenewalBadge row={row} /></td>
            <td>{row.pendingRequest ? <span className="ap-badge pending">{money(row.pendingRequest.amountKobo)} pending</span> : '—'}</td>
          </tr>)}</tbody></table>
      </div>
    </div>

    {loading ? <div className="ap-panel"><div className="ap-empty">Loading subscription data…</div></div> : <RequestQueue statusFilter={statusFilter} setStatusFilter={setStatusFilter} requests={requests} busyId={busyId} onApprove={approve} onReject={setRejectTarget} />}

    {rejectTarget && <div className="ap-modal-backdrop" onClick={() => setRejectTarget(null)}><div className="ap-modal" style={{ maxWidth: 430 }} onClick={e => e.stopPropagation()}>
      <div className="ap-modal-head"><h2 style={{ fontSize: 16 }}>Reject payment request</h2><button className="ap-modal-close" onClick={() => setRejectTarget(null)}>✕</button></div>
      <textarea className="ap-confirm-input" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason (optional, shown in the request history)" />
      <div className="ap-btn-row" style={{ marginTop: 12 }}><button className="ap-btn ap-btn-reject" onClick={reject} disabled={busyId === rejectTarget.requestId}>Reject request</button><button className="ap-btn ap-btn-ghost" onClick={() => setRejectTarget(null)}>Cancel</button></div>
    </div></div>}
  </>;
}
